import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { useState } from "react";
import { Check, ArrowRight, Sparkles, Star } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { useCurrency } from "@/lib/ecom/currency";
import {
  PLANS,
  PLANS_VI,
  getPlanMonthly,
  formatPlanPrice,
  defaultPlanPriceText,
} from "@/components/marketing/pricing-data";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/pricing")({
  head: () => ({
    meta: [
      { title: "Bảng giá — banana.cards" },
      { name: "description", content: "Giá đơn giản, minh bạch cho cá nhân và đội nhóm. Bắt đầu miễn phí và mở rộng với AI khi bạn cần." },
      { property: "og:title", content: "Bảng giá banana.cards" },
      { property: "og:description", content: "Miễn phí vĩnh viễn cho cá nhân. Pro AI cho người kết nối nghiêm túc. Gói Đội nhóm tính theo chỗ ngồi. Doanh nghiệp theo yêu cầu." },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/pricing", "vi"),
  }),
  component: PricingPage,
});

function PricingPage() {
  const [interval, setInterval] = useState<"month" | "year">("year");
  const [seats, setSeats] = useState(3);
  const { country } = useCurrency();

  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 pt-20 pb-12 text-center md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Bảng giá</p>
        <h1 className="mx-auto mt-3 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
          Bắt đầu miễn phí. Phát triển khi <em className="italic">bạn sẵn sàng.</em>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
          Không cần thẻ tín dụng. Hủy bất cứ lúc nào. Giá tự động cập nhật theo khu vực của bạn.
        </p>

        <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-muted p-1">
          <button
            type="button"
            onClick={() => setInterval("month")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
              interval === "month" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Hàng tháng
          </button>
          <button
            type="button"
            onClick={() => setInterval("year")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              interval === "year" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
          >
            Hàng năm
            <span className="rounded-full bg-brand-lime px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
              Tiết kiệm
            </span>
          </button>
        </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PLANS_VI.map((p) => {
            const Icon = p.icon;
            const isFree = p.key === "free";
            const isEnterprise = p.key === "enterprise";
            const isPaid = p.key === "pro_ai" || p.key === "teams";
            const paidKey = (p.key === "pro_ai" || p.key === "teams") ? p.key : null;
            const priceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval))
              : defaultPlanPriceText("pro_ai", interval);
            const seatTotalText = paidKey
              ? formatPlanPrice(
                  country,
                  (interval === "year"
                    ? getPlanMonthly(paidKey, country.code, "year") / 2
                    : getPlanMonthly(paidKey, country.code, interval)) * seats,
                )
              : "";
            const firstYearPriceText = paidKey
              ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, interval) / 2)
              : "";
            void isPaid;

            return (
              <div
                key={p.key}
                className={`relative flex flex-col rounded-2xl bg-muted p-6 transition ${
                  p.popular ? "ring-2 ring-foreground" : ""
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-6 rounded-full bg-foreground px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-background">
                    Phổ biến nhất
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-card">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="text-lg font-bold">{p.name}</h3>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{p.tagline}</p>

                <div className="mt-6 min-h-[80px]">
                  {isFree ? (
                    <>
                      <p className="font-display text-5xl leading-none">Miễn phí</p>
                      <p className="mt-1 text-xs text-muted-foreground">Vĩnh viễn, dành cho cá nhân.</p>
                    </>
                  ) : isEnterprise ? (
                    <>
                      <p className="font-display text-4xl leading-none">Tùy chỉnh</p>
                      <p className="mt-2 text-xs text-muted-foreground">Giá và SLA theo nhu cầu.</p>
                    </>
                  ) : (
                    <>
                      {interval === "year" ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-display text-lg leading-none tracking-tight text-muted-foreground line-through sm:text-xl">
                            {priceText}
                          </span>
                          <div className="flex flex-wrap items-baseline gap-x-1">
                            <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                              {firstYearPriceText}
                            </span>
                            <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                              /{p.perSeat ? "chỗ/" : ""}tháng
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                          <span className="font-display text-4xl leading-none tracking-tight sm:text-5xl">
                            {priceText}
                          </span>
                          <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                            /{p.perSeat ? "chỗ/" : ""}tháng
                          </span>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {interval === "year" ? "Thanh toán hàng năm" : "Thanh toán hàng tháng"}
                      </p>
                      {interval === "year" ? (
                        <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-brand-lime/30 px-2 py-0.5 text-[10px] font-bold text-brand-lime-foreground">
                          Giảm 50% năm đầu
                        </p>
                      ) : (
                        <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-brand-lime-foreground">
                          Giảm 50% khi chọn thanh toán hàng năm
                        </p>
                      )}
                      {p.perSeat && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Chỗ ngồi</span>
                          <div className="flex items-center overflow-hidden rounded-full bg-card ring-1 ring-foreground/10">
                            <button aria-label="Giảm số chỗ ngồi" onClick={() => setSeats((s) => Math.max(3, s - 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">–</button>
                            <span className="w-8 border-x border-foreground/10 text-center text-xs font-bold">{seats}</span>
                            <button aria-label="Tăng số chỗ ngồi" onClick={() => setSeats((s) => Math.min(500, s + 1))} className="h-7 w-7 text-sm font-bold hover:bg-card">+</button>
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            ≈ {seatTotalText}/tháng
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <CtaLink
                  to={isEnterprise ? "/vi/contact" : "/signup"}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition ${
                    p.popular
                      ? "bg-brand-lime text-brand-lime-foreground hover:scale-[1.02]"
                      : "bg-foreground text-background hover:opacity-90"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="h-4 w-4" />
                </CtaLink>

                <ul className="mt-6 space-y-2.5 border-t border-foreground/5 pt-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                       {f.startsWith("Mọi tính năng") ? (
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{f}</span>
                      ) : (
                        <>
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                          <span>{f}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-card p-8 md:p-12">
          <div className="grid items-center gap-8 md:grid-cols-[1.4fr_2fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Cho cả đội ngũ</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Xây dựng cho <em className="italic">đội ngũ thường xuyên gặp gỡ.</em>
              </h2>
              <p className="mt-4 text-sm text-muted-foreground">
                Triển khai thẻ có thương hiệu, CRM dùng chung và theo dõi bằng AI trên toàn tổ chức. Giảm giá cho 25+ chỗ ngồi.
              </p>
              <div className="mt-6 flex gap-2">
                <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background hover:opacity-90">
                  Liên hệ bán hàng <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                "Mẫu thẻ có thương hiệu của đội",
                "Liên hệ & pipeline dùng chung",
                "Quyền quản trị & SSO",
                "Đặt hàng thẻ NFC số lượng lớn",
                "Tên miền tùy chỉnh & liên kết riêng",
                "Hỗ trợ ưu tiên qua điện thoại & chat",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 rounded-xl bg-muted p-3 text-sm">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="font-semibold">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-4 py-24 md:px-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Câu hỏi thường gặp</p>
        <h2 className="mt-2 text-center font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Câu hỏi, <em className="italic">đã được giải đáp.</em>
        </h2>
        <div className="mt-10 space-y-3">
          {[
            { q: "Gói miễn phí có thực sự miễn phí vĩnh viễn không?", a: "Có. Bạn có thể dùng banana.cards như một danh thiếp kỹ thuật số và CRM cơ bản miễn phí, vĩnh viễn — không cần thẻ tín dụng." },
            { q: "Tôi có cần mua thẻ NFC không?", a: "Không — thẻ kỹ thuật số của bạn hoạt động qua QR, liên kết, chữ ký email hoặc Apple/Google Wallet. Thẻ NFC là phụ kiện cao cấp tùy chọn." },
            { q: "Tôi có thể đổi gói sau không?", a: "Hoàn toàn được. Nâng cấp, hạ cấp hoặc tạm dừng bất cứ lúc nào từ cài đặt workspace." },
            { q: "AI được tính như thế nào?", a: "Pro AI bao gồm 200 email AI, 100 lần cải thiện và 50 lần quét thẻ mỗi tháng. Đội nhóm có giới hạn cao hơn, Doanh nghiệp không giới hạn." },
            { q: "Bạn có giảm giá cho sinh viên hoặc phi lợi nhuận không?", a: "Có — hãy liên hệ với đội ngũ chúng tôi và chúng tôi sẽ sắp xếp." },
            { q: "Dữ liệu của tôi có an toàn không?", a: "Tất cả dữ liệu được mã hóa khi truyền và khi lưu. Gói Đội nhóm và Doanh nghiệp hỗ trợ SSO và DPA đã ký." },
          ].map((f, i) => (
            <details key={i} className="group rounded-2xl bg-muted p-5">
              <summary className="flex cursor-pointer items-center justify-between text-sm font-bold">
                {f.q}
                <span className="ml-3 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
          <div className="mx-auto inline-flex items-center gap-1 rounded-full bg-background/10 px-3 py-1 text-xs font-bold text-brand-lime ring-1 ring-background/10">
            <Star className="h-3 w-3 fill-current" /> 4.9 / 5 trên hơn 12.000 đánh giá
          </div>
          <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Dùng thử banana.cards <em className="italic text-brand-lime">miễn phí</em> trong 14 ngày.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Bắt đầu miễn phí
            </CtaLink>
            <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              Liên hệ bán hàng
            </Link>
          </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}